import { supabase } from "@/integrations/supabase/client";
import { withPermissionHandling } from "@/lib/permissionService";

export interface Proposal {
  id: string;
  client_id: string;
  title: string;
  description?: string | null;
  value?: number | null;
  final_value?: number | null;
  currency: string;
  status: string;
  probability: number;
  sent_date?: string | null;
  response_deadline?: string | null;
  created_by?: string | null;
  org_id?: string | null;
  tags?: string[] | null;
  led_by?: string | null;
  created_at: string;
  updated_at: string;
  clients?: { name: string; id: string };
}

export interface CreateProposalData {
  client_id: string;
  title: string;
  description?: string;
  value?: number;
  final_value?: number;
  currency?: string;
  status?: string;
  probability?: number;
  sent_date?: string;
  response_deadline?: string;
  tags?: string[];
  led_by?: string;
}

export interface UpdateProposalData extends Partial<CreateProposalData> {
  id: string;
}

export const proposalService = {
  // Get all proposals
  getProposals: async (): Promise<Proposal[]> => {
    return await withPermissionHandling(async () => {
      return await supabase
        .from('proposals')
        .select(`
          *,
          clients:client_id(name, id)
        `)
        .order('updated_at', { ascending: false });
    });
  },

  // Get proposal by ID
  getProposalById: async (id: string): Promise<Proposal | null> => {
    const { data, error } = await supabase
      .from('proposals')
      .select(`
        *,
        clients:client_id(name, id)
      `)
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Create new proposal
  createProposal: async (proposalData: CreateProposalData): Promise<Proposal> => {
    return await withPermissionHandling(async () => {
      return await supabase
        .from('proposals')
        .insert([proposalData])
        .select(`
          *,
          clients:client_id(name, id)
        `)
        .single();
    });
  },

  // Update proposal
  updateProposal: async ({ id, ...updateData }: UpdateProposalData): Promise<Proposal> => {
    return await withPermissionHandling(async () => {
      return await supabase
        .from('proposals')
        .update(updateData)
        .eq('id', id)
        .select(`
          *,
          clients:client_id(name, id)
        `)
        .single();
    });
  },

  // Delete proposal
  deleteProposal: async (id: string): Promise<void> => {
    await withPermissionHandling(async () => {
      return await supabase
        .from('proposals')
        .delete()
        .eq('id', id);
    });
  },

  // Get proposal statistics
  getProposalStats: async () => {
    const { data, error } = await supabase.rpc('get_proposal_statistics');
    if (error) throw error;
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  }
};